/**
 * Router Guards
 *
 * IMPORTANT: Authentication is ALWAYS required - no dev mode bypass.
 * This ensures auth works the same in development and production.
 */
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores'

export async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const authStore = useAuthStore()

  // Wait for auth to initialize
  if (authStore.isLoading) {
    await new Promise<void>((resolve) => {
      const unwatch = setInterval(() => {
        if (!authStore.isLoading) {
          clearInterval(unwatch)
          resolve()
        }
      }, 100)
    })
  }

  // Check if route requires authentication
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const requiresGuest = to.matched.some((record) => record.meta.requiresGuest)

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login with return URL
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    })
    return
  }

  if (requiresGuest && authStore.isAuthenticated) {
    // Redirect to dashboard if already logged in
    next({ name: 'dashboard' })
    return
  }

  // Check role-based access
  const requiredRoles = to.meta.roles as string[] | undefined
  if (requiredRoles && requiredRoles.length > 0) {
    if (!authStore.hasRole(requiredRoles as Parameters<typeof authStore.hasRole>[0])) {
      // Redirect to dashboard with no permission
      next({ name: 'dashboard' })
      return
    }
  }

  // Path B: canvas builder access gate. Client portal users without the
  // can_use_canvas_builder flag bounce back to the simpler Phase 1 builder
  // at /reports/builder. Team users always pass.
  if (to.meta.requiresCanvasBuilder && !authStore.canUseCanvasBuilder) {
    next({ name: 'report-builder' })
    return
  }

  next()
}

export function setPageTitle(to: RouteLocationNormalized): void {
  const appName = 'Consulting CRM'
  const pageTitle = to.meta.title as string | undefined

  document.title = pageTitle ? `${pageTitle} | ${appName}` : appName
}
